import { useCallback, useState } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';
import { Modal } from 'cpool-components/general/Modal';
import { ModalVariant } from 'cpool-components/general/Modal/types';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, USER_RESOLVERS } from 'cpool-constants';
import { schemaUserResolver_GetPossibleDelegates } from 'cpool-tables/backend-resolvers';
import { getDateString } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuCalendarRange, LuCheck, LuClock, LuPlus, LuSave, LuTrash2, LuX } from 'react-icons/lu';

import { AdviserAbsenceEdit } from './AdviserAbsenceEdit';
import { DelegateName } from './DelegateName';
import { useDeleteAdviserAbsenceMutation } from './hooks/useDeleteAdviserAbsenceMutation';
import { useSetAdviserAbsenceMutation } from './hooks/useSetAdviserAbsenceMutation';
import type { Adviser_Schema } from './types/AdviserSchema';
import { EndRepresentationDialog } from '~/components/Dialogs/EndRepresentationDialog';
import { useEndRepresentationMutation } from '~/components/Dialogs/hooks/useEndRepresentationMutation';
import { useUserData } from '~/components/Header/menu/hooks/useUserData';
import { IconBadge } from '~/features/dashboard/components/IconBadge';
import { dashboardSearchSchema } from '~/features/dashboard/dashboardSearch';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { dateFormat } from '~/lib/i18n';
import { useAggregateQuery } from '~/queryUtils/useQuery';
import {
    getActiveEndRepresentation,
    hasManualDelegation,
    isAbsenceActiveNow,
    isAbsenceFuture,
    isAbsenceUpcoming,
} from '~/utils/representation';

type Props = {
    userId?: string;
};

export const AdviserAbsence: React.FC<Props> = ({ userId }) => {
    const { t } = useTranslation();
    const { data: user } = useUserData(userId);
    const { adviserAbsence } = dashboardSearchSchema.parse(useSearch({ strict: false }));
    const navigate = useNavigate();
    const [endDialogOpen, setEndDialogOpen] = useState(false);

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const lastAbsence = user?.absence?.slice(-1)[0];
    const nowMs = Date.now();
    const isActiveAbsence = lastAbsence ? isAbsenceActiveNow(lastAbsence, nowMs) : false;
    const isFutureAbsence = lastAbsence ? isAbsenceFuture(lastAbsence, nowMs) : false;
    const hasUpcomingAbsence = Boolean(lastAbsence && isAbsenceUpcoming(lastAbsence, nowMs));
    const showManualDelegation = Boolean(user && hasManualDelegation(user) && !hasUpcomingAbsence);
    const hasRepresentationEntry = hasUpcomingAbsence || showManualDelegation;
    const activeRepresentation = user ? getActiveEndRepresentation(user, nowMs) : undefined;
    const appointerName = user ? `${user.firstName} ${user.lastName}` : undefined;

    const [absenceFrom, setAbsenceFrom] = useState<Date>(today);
    const [absenceUntil, setAbsenceUntil] = useState<Date>(tomorrow);

    const { permissions } = useAclPermissions();
    const { data: delegates, isLoading } = useAggregateQuery({
        acl: {},
        aggregateName: AGGREGATE_NAMES.USER,
        resolverName: USER_RESOLVERS.getPossibleDelegates,
        schema: schemaUserResolver_GetPossibleDelegates,
        searchParams: {
            absenceFrom: getDateString(absenceFrom),
            absenceUntil: getDateString(absenceUntil),
        },
    });

    const [delegate, setDelegate] = useState<Adviser_Schema | undefined>();

    const closeAbsence = useCallback(() => {
        navigate({
            replace: true,
            search: (previous) => ({ ...previous, adviserAbsence: undefined }),
            to: '.',
        });
    }, [navigate]);

    const {
        error: saveError,
        mutate: saveAbsenceData,
        reset: resetSaveError,
    } = useSetAdviserAbsenceMutation({
        absenceFrom,
        absenceUntil,
        onSuccess: closeAbsence,
    });

    const resetInputs = () => {
        setDelegate(undefined);
        setAbsenceFrom(today);
        setAbsenceUntil(tomorrow);
    };

    const cancelAbsence = () => {
        closeAbsence();
        resetInputs();
        resetSaveError();
    };

    const isSetAdviserAbsenceDisabled = !permissions.commands[AGGREGATE_NAMES.USER].setAdviserAbsence;

    const { mutate: deleteLastAbsenceData } = useDeleteAdviserAbsenceMutation({
        eventId: lastAbsence?.eventId,
        resetInputs,
    });

    const { isPending: isEndingRepresentation, mutate: endRepresentation } = useEndRepresentationMutation({
        onSuccess: () => {
            setEndDialogOpen(false);
            resetInputs();
        },
        reactivateLeadTrack: false,
        userId: userId ?? '',
    });

    const isDeleteAdviserAbsenceDisabled = !permissions.commands[AGGREGATE_NAMES.USER].deleteAdviserAbsence;

    const openEndDialog = () => {
        if (activeRepresentation) {
            setEndDialogOpen(true);
        }
    };

    const renderFooterAction = () => {
        if (!hasRepresentationEntry) {
            return (
                <Button
                    variant={ButtonVariant.SECONDARY}
                    iconLeft={<LuPlus />}
                    onClick={() => navigate({ search: (previous) => ({ ...previous, adviserAbsence: true }), to: '.' })}
                    className="w-full"
                >
                    {t('DASHBOARD.ADVISER_ABSENCE.ADD')}
                </Button>
            );
        }

        if (isActiveAbsence || showManualDelegation) {
            return (
                <Button
                    variant={ButtonVariant.SECONDARY}
                    iconLeft={<LuCheck className="text-midnight" />}
                    onClick={openEndDialog}
                    disabled={!activeRepresentation}
                    className="w-full"
                >
                    {t('DASHBOARD.ADVISER_ABSENCE.END_BUTTON')}
                </Button>
            );
        }

        return (
            <Button
                variant={ButtonVariant.SECONDARY}
                iconLeft={<LuTrash2 className="text-status-red-text" />}
                onClick={() => deleteLastAbsenceData({ userId })}
                disabled={isDeleteAdviserAbsenceDisabled || !isFutureAbsence}
                className="w-full"
            >
                {t('DASHBOARD.ADVISER_ABSENCE.DELETE')}
            </Button>
        );
    };

    return (
        <section className="py-2.5 px-5 min-h-40 flex flex-col">
            <div className="flex items-center pb-2 border-b-2 border-white min-h-[38px]">
                <div className="flex items-center gap-4">
                    <IconBadge icon={<LuCalendarRange />} className="text-blue-text bg-blue-cloud" />
                    <h6 className="text-lg font-medium text-midnight">{t('DASHBOARD.ADVISER_ABSENCE.TITLE')}</h6>
                </div>
            </div>
            <div className="flex-1">
                {!hasRepresentationEntry ? (
                    <div className="flex flex-col items-center gap-2 mt-8">
                        <div className="text-sm text-midnight">{t('DASHBOARD.ADVISER_ABSENCE.NO_ABSENCE')}</div>
                    </div>
                ) : (
                    <div className="py-3 space-y-2 mt-5">
                        <div className="flex items-center gap-1">
                            <p className="text-sm text-midnight">{t('DASHBOARD.ADVISER_ABSENCE.DELEGATE')}</p>
                            <p className="text-sm text-midnight">
                                {showManualDelegation && user?.delegateId ? (
                                    <DelegateName delegateId={user.delegateId} />
                                ) : (
                                    <DelegateName delegateId={lastAbsence?.delegateId} />
                                )}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                            <div className="flex items-center gap-2">
                                <span className="text-blue-text">
                                    <LuClock />
                                </span>
                                <p className="text-sm text-midnight">
                                    {showManualDelegation && user?.delegateAppointedAt
                                        ? dateFormat(user.delegateAppointedAt)
                                        : dateFormat(absenceFrom)}
                                </p>
                                <p className="text-sm text-midnight mx-2">{t('DASHBOARD.ADVISER_ABSENCE.UNTIL')}</p>
                                <span className="text-blue-text">
                                    <LuClock />
                                </span>
                                <p className="text-sm text-midnight">
                                    {showManualDelegation
                                        ? t('TOP_HEADER.REPRESENTATION.UNLIMITED')
                                        : dateFormat(absenceUntil)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="pt-2 border-t-2 border-white">{renderFooterAction()}</div>

            <Modal
                variant={ModalVariant.SIDEBAR}
                side="right"
                width="500px"
                open={adviserAbsence === true}
                onClose={cancelAbsence}
                title={t('DASHBOARD.ADVISER_ABSENCE.ADD_TITLE')}
            >
                <AdviserAbsenceEdit
                    absenceFrom={absenceFrom}
                    absenceUntil={absenceUntil}
                    delegates={delegates}
                    isLoading={isLoading}
                    setAbsenceFrom={setAbsenceFrom}
                    setAbsenceUntil={setAbsenceUntil}
                    setDelegate={setDelegate}
                />
                {saveError && <p className="mt-4 text-sm text-status-red-text">{saveError.message}</p>}
                <div className="flex gap-2 mt-6">
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        iconLeft={<LuX />}
                        onClick={cancelAbsence}
                        className="flex-1"
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        iconLeft={<LuSave />}
                        onClick={() => saveAbsenceData({ delegate, userId })}
                        disabled={isSetAdviserAbsenceDisabled}
                        className="flex-1"
                    >
                        {t('BUTTONS.SAVE')}
                    </Button>
                </div>
            </Modal>

            {activeRepresentation ? (
                <EndRepresentationDialog
                    appointerName={appointerName}
                    isPending={isEndingRepresentation}
                    open={endDialogOpen}
                    onClose={() => setEndDialogOpen(false)}
                    onConfirm={endRepresentation}
                    representation={activeRepresentation}
                    showLeadTrackAlert={false}
                />
            ) : null}
        </section>
    );
};
